import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import MSINav from "./MSINav";
import { optionPlayScore, optionPlayLabel } from "./msiOptionLogic";
import { contractLabel } from "./msiContractSelector";
import { buildPosition } from "./msiPositionBuilder";
import { addToWatchlist } from "./msiWatchlistManager";

const panel = {
  background: "#171717",
  border: "1px solid #2A2A2A",
  borderRadius: 12,
  padding: 18,
};

const inputStyle = {
  background: "#0B0B0B",
  border: "1px solid #2A2A2A",
  color: "white",
  borderRadius: 8,
  padding: "10px 12px",
};

const cellClip = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const compactText = {
  fontSize: 14,
  lineHeight: 1.2,
};

const colorForBias = (bias) =>
  bias === "CALL" ? "#22c55e" : bias === "PUT" ? "#ef4444" : "#f59e0b";

const tvUrl = (ticker) =>
  `https://www.tradingview.com/chart/?symbol=${encodeURIComponent(ticker)}`;

const COLUMN_DEFS = [
  { key: "select", label: "Select", width: "48px", locked: true },
  { key: "ticker", label: "Ticker", width: "70px", locked: true },
  { key: "bias", label: "Bias", width: "72px" },
  { key: "play", label: "Play", width: "76px" },
  { key: "contract", label: "Contract", width: "112px" },
  { key: "price", label: "Price", width: "76px" },
  { key: "msi", label: "MSI", width: "52px" },
  { key: "opportunity", label: "Opp", width: "82px" },
  { key: "target", label: "Target", width: "132px" },
  { key: "sector", label: "Sector", width: "112px" },
  { key: "confluence", label: "Conf", width: "66px" },
  { key: "structure", label: "Structure", width: "90px" },
  { key: "candle", label: "Candle", width: "112px" },
  { key: "chart", label: "Chart", width: "140px" },
];

const DEFAULT_VISIBLE = {
  select: true,
  ticker: true,
  bias: true,
  play: true,
  contract: true,
  price: true,
  msi: true,
  opportunity: true,
  target: true,
  sector: true,
  confluence: true,
  structure: true,
  candle: true,
  chart: true,
};

function Pill({ children, color }) {
  return (
    <span
      style={{
        background: color || "#2A2A2A",
        color: "white",
        padding: "3px 7px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 800,
      }}
    >
      {children}
    </span>
  );
}

function fmt(n, d = 2) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "-";
  return Number(n).toFixed(d);
}

function fmtTarget(target) {
  if (!target) return "-";

  const text = String(target);

  if (text.includes("No overhead target")) return "No overhead target";
  if (text.includes("No downside target")) return "No downside target";

  const m = text.match(/(.*) @ ([0-9.]+)/);
  if (!m) return text;

  let label = m[1];

  label = label
    .replace("Prev_Day_High", "PD High")
    .replace("Prev_Day_Low", "PD Low")
    .replace("Prev_Week_High", "PW High")
    .replace("Prev_Week_Low", "PW Low")
    .replace("D_50MA", "D50")
    .replace("D_200MA", "D200")
    .replace("3D_50MA", "3D50")
    .replace("3D_200MA", "3D200")
    .replace("4H_10MA", "4H10")
    .replace("4H_20MA", "4H20")
    .replace("4H_50MA", "4H50")
    .replace("4H_200MA", "4H200");

  return `${label} @ ${Number(m[2]).toFixed(2)}`;
}

function fmtStage(stage) {
  const s = String(stage || "-");
  return s
    .replace("3D_50MA_RECLAIM", "Reclaim")
    .replace("3D_50MA_ATTACK", "Attack")
    .replace("WEAK_BELOW_CHANNEL", "Weak")
    .replace("MIXED_3D_STRUCTURE", "Mixed")
    .replace("CHANNEL_RECOVERY", "Channel")
    .replace("BOTTOMING", "Bottoming")
    .replace("EARLY_RECOVERY", "Early Rec.");
}

function patternForTf(row, tf) {
  if (tf === "weekly") return row.weekly_candle_pattern;
  if (tf === "tf3d") return row.tf3d_candle_pattern;
  if (tf === "daily") return row.daily_candle_pattern;
  if (tf === "tf4h") return row.tf4h_candle_pattern;
  return row.active_candle_patterns || "";
}

function candleDisplay(row) {
  return row.pattern_alignment || row.candle_bias || "-";
}

function patternLabel(pattern) {
  if (!pattern || pattern === "ALL") return pattern || "ALL";

  return String(pattern)
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function playColor(label) {
  if (label === "A+" || label === "A") return "#22c55e";
  if (label === "B+" || label === "B") return "#f59e0b";
  if (label === "WAIT") return "#737373";
  return "#A0A0A0";
}

function candleColor(value) {
  const s = String(value || "").toUpperCase();

  if (s.includes("BULLISH")) return "#22c55e";
  if (s.includes("BEARISH")) return "#ef4444";
  if (s.includes("MIXED")) return "#A0A0A0";

  return "#D4D4D4";
}

function opportunityValue(r) {
  const n = Number(r.recovery_3d_expansion_room_pct);
  if (Number.isNaN(n)) return "-";

  const signed = r.bias === "PUT" ? -Math.abs(n) : Math.abs(n);
  return `${signed > 0 ? "+" : ""}${signed.toFixed(1)}%`;
}

function opportunityColor(r) {
  if (r.bias === "PUT") return "#ef4444";
  if (r.bias === "CALL") return "#22c55e";
  return "#A0A0A0";
}

function addPositionFromPlay(play) {
  const existing = JSON.parse(localStorage.getItem("msi_open_positions") || "[]");

  const position = buildPosition({
    ...play,
    contract: contractLabel(play),
    target: fmtTarget(play.target),
    price_target: fmtTarget(play.target),
    stage: play.recovery_3d_stage || play.setup_type || play.stage,
    dte: play.dte || 0,
  });

  const exists = existing.some(
    (p) =>
      p.ticker === position.ticker &&
      p.contract === position.contract &&
      String(p.status || "").toUpperCase() !== "CLOSED"
  );

  if (exists) return { added: false, ticker: position.ticker, contract: position.contract };

  const next = [position, ...existing];
  localStorage.setItem("msi_open_positions", JSON.stringify(next));
  return { added: true, ticker: position.ticker, contract: position.contract };
}

export default function MSIUniverse() {
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [bias, setBias] = useState("ALL");
  const [patternTf, setPatternTf] = useState("ALL");
  const [pattern, setPattern] = useState("ALL");
  const [chartBias, setChartBias] = useState("ALL");
  const [sector, setSector] = useState("ALL");
  const [minMsi, setMinMsi] = useState("ALL");
  const [sortBy, setSortBy] = useState("option_score");
  const [selected, setSelected] = useState({});
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("msi_universe_view") || "grid");
  const [showColumns, setShowColumns] = useState(false);
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      return {
        ...DEFAULT_VISIBLE,
        ...(JSON.parse(localStorage.getItem("msi_universe_columns")) || {}),
      };
    } catch {
      return DEFAULT_VISIBLE;
    }
  });

  useEffect(() => {
    fetch(`/data/market-desk.json?t=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
    })
      .then((r) => r.json())
      .then((data) => setRows(Array.isArray(data) ? data : [data]))
      .catch((err) => console.error("MSI Universe load failed:", err));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setBias(params.get("bias") || "ALL");
    setPattern(params.get("pattern") || "ALL");
    setPatternTf(params.get("patternTf") || "ALL");
    setSector(params.get("sector") || "ALL");
  }, [location.search]);

  useEffect(() => {
    localStorage.setItem("msi_universe_view", viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem("msi_universe_columns", JSON.stringify(visibleCols));
  }, [visibleCols]);

  const sectors = useMemo(
    () => ["ALL", ...Array.from(new Set(rows.map((r) => r.sector).filter(Boolean))).sort()],
    [rows]
  );

  const patterns = useMemo(() => {
    const vals = new Set();

    rows.forEach((r) => {
      [
        "weekly_candle_pattern",
        "tf3d_candle_pattern",
        "daily_candle_pattern",
        "tf4h_candle_pattern",
      ].forEach((k) => {
        if (r[k] && r[k] !== "NONE" && r[k] !== "-") vals.add(r[k]);
      });

      if (r.pattern_alignment && r.pattern_alignment !== "-") vals.add(r.pattern_alignment);
    });

    return ["ALL", ...Array.from(vals).sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    let out = [...rows];

    if (q.trim()) {
      const needle = q.trim().toUpperCase();
      out = out.filter(
        (r) =>
          String(r.ticker || "").toUpperCase().includes(needle) ||
          String(r.sector || "").toUpperCase().includes(needle) ||
          String(r.theme || "").toUpperCase().includes(needle) ||
          String(r.setup_type || "").toUpperCase().includes(needle) ||
          String(r.pattern_summary || "").toUpperCase().includes(needle)
      );
    }

    if (bias !== "ALL") out = out.filter((r) => r.bias === bias);
    if (sector !== "ALL") out = out.filter((r) => r.sector === sector);
    if (chartBias !== "ALL") {
      out = out.filter((r) => String(r.chart_pattern_bias) === chartBias);
    }

    if (minMsi !== "ALL") {
      const floor = Number(minMsi);
      out = out.filter((r) => Number(r.msi_score || 0) >= floor);
    }

    if (pattern !== "ALL") {
      out = out.filter((r) => {
        const alignment = String(r.pattern_alignment || "");
        if (alignment === pattern) return true;

        return patternTf === "ALL"
          ? String(r.active_candle_patterns || "").includes(pattern)
          : patternForTf(r, patternTf) === pattern;
      });
    }

    out.sort((a, b) => {
      if (sortBy === "option_score") return optionPlayScore(b) - optionPlayScore(a);
      return Number(b[sortBy] || 0) - Number(a[sortBy] || 0);
    });

    return out;
  }, [rows, q, bias, sector, chartBias, minMsi, patternTf, pattern, sortBy]);

  const activeColumns = COLUMN_DEFS.filter((c) => c.locked || visibleCols[c.key]);
  const grid = activeColumns.map((c) => c.width).join(" ");
  const minWidth = activeColumns.reduce((sum, c) => sum + Number(c.width.replace("px", "")), 0) + 12 * activeColumns.length;

  const selectedRows = filtered.filter((r) => selected[r.ticker]);
  const selectedCount = selectedRows.length;

  function toggleSelected(ticker) {
    setSelected((prev) => ({ ...prev, [ticker]: !prev[ticker] }));
  }

  function clearSelected() {
    setSelected({});
  }

  function selectVisible() {
    const next = {};
    filtered.forEach((r) => {
      next[r.ticker] = true;
    });
    setSelected(next);
  }

  function addSelectedPositions() {
    if (!selectedRows.length) {
      alert("Select at least one play first");
      return;
    }

    let added = 0;
    let skipped = 0;

    selectedRows.forEach((r) => {
      const result = addPositionFromPlay(r);
      if (result.added) added += 1;
      else skipped += 1;
    });

    clearSelected();
    alert(`Added ${added} position(s). Skipped ${skipped} duplicate(s).`);
  }

  function addSelectedToWatchlist() {
    if (!selectedRows.length) {
      alert("Select at least one play first");
      return;
    }

    let added = 0;
    let skipped = 0;

    selectedRows.forEach((r) => {
      const result = addToWatchlist(r, {
        contractLabel,
        fmtTarget,
        optionPlayScore,
        optionPlayLabel,
        fmtStage,
      });

      if (result.added) added += 1;
      else skipped += 1;
    });

    clearSelected();
    alert(`Added ${added} to Watchlist. Skipped ${skipped} duplicate(s).`);
  }

  function toggleColumn(key) {
    const def = COLUMN_DEFS.find((c) => c.key === key);
    if (def?.locked) return;

    setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function resetColumns() {
    setVisibleCols(DEFAULT_VISIBLE);
  }

  function Cell({ col, row }) {
    const playLabel = optionPlayLabel(row);
    const playScore = optionPlayScore(row);

    if (col.key === "select") {
      return (
        <div>
          <input
            type="checkbox"
            checked={!!selected[row.ticker]}
            onChange={() => toggleSelected(row.ticker)}
            style={{ transform: "scale(1.05)", cursor: "pointer" }}
          />
        </div>
      );
    }

    if (col.key === "ticker") {
      return (
        <div style={{ fontWeight: 900, ...cellClip }}>
          <Link to={`/msi/ticker/${row.ticker}`} style={{ color: "white", textDecoration: "none" }}>
            {row.ticker}
          </Link>{" "}
          <a
            href={tvUrl(row.ticker)}
            target="_blank"
            rel="noopener noreferrer"
            title="Open in TradingView"
            style={{ color: "#22c55e", textDecoration: "none", marginLeft: 5 }}
          >
            ↗
          </a>
        </div>
      );
    }

    if (col.key === "bias") return <Pill color={colorForBias(row.bias)}>{row.bias}</Pill>;

    if (col.key === "play") {
      return (
        <div style={{ color: playColor(playLabel), fontWeight: 900 }}>
          {playLabel} · {playScore}
        </div>
      );
    }

    if (col.key === "contract") {
      return (
        <div title={contractLabel(row)} style={{ color: "#D4D4D4", fontWeight: 800, ...cellClip }}>
          {contractLabel(row)}
        </div>
      );
    }

    if (col.key === "price") return <div>${fmt(row.price, 2)}</div>;
    if (col.key === "msi") return <div>{Math.round(Number(row.msi_score || 0))}</div>;

    if (col.key === "opportunity") {
      return (
        <div title="Signed percent distance to the current technical target" style={{ color: opportunityColor(row), fontWeight: 900 }}>
          {opportunityValue(row)}
        </div>
      );
    }

    if (col.key === "target") {
      return (
        <div title={fmtTarget(row.target)} style={{ color: "#A0A0A0", ...cellClip }}>
          {fmtTarget(row.target)}
        </div>
      );
    }

    if (col.key === "sector") return <div style={{ color: "#D4D4D4", ...cellClip }}>{row.sector || "-"}</div>;
    if (col.key === "confluence") return <div>{Math.round(Number(row.confidence || 0))}</div>;
    if (col.key === "structure") return <div title={row.recovery_3d_stage || "-"} style={cellClip}>{fmtStage(row.recovery_3d_stage)}</div>;

    if (col.key === "candle") {
      return (
        <div title={candleDisplay(row)} style={{ ...cellClip, color: candleColor(candleDisplay(row)), fontWeight: 900 }}>
          {candleDisplay(row)}
        </div>
      );
    }

    if (col.key === "chart") {
      return (
        <div
          title={row.chart_pattern_summary || "-"}
          style={{
            ...cellClip,
            fontWeight: 900,
            color:
              row.chart_pattern_bias === "BULLISH"
                ? "#22c55e"
                : row.chart_pattern_bias === "BEARISH"
                ? "#ef4444"
                : "#f59e0b",
          }}
        >
          {row.chart_pattern_alignment || "-"}
        </div>
      );
    }

    return null;
  }

  function CardView() {
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: 14,
        }}
      >
        {filtered.map((r) => {
          const playLabel = optionPlayLabel(r);
          const playScore = optionPlayScore(r);

          return (
            <div key={`${r.ticker}-${r.sector}-${r.setup_type}`} style={{ ...panel, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 10 }}>
                <div>
                  <Link to={`/msi/ticker/${r.ticker}`} style={{ color: "white", textDecoration: "none", fontSize: 22, fontWeight: 900 }}>
                    {r.ticker}
                  </Link>
                  <a href={tvUrl(r.ticker)} target="_blank" rel="noopener noreferrer" style={{ color: "#22c55e", marginLeft: 8, textDecoration: "none" }}>
                    ↗
                  </a>
                </div>
                <input
                  type="checkbox"
                  checked={!!selected[r.ticker]}
                  onChange={() => toggleSelected(r.ticker)}
                  style={{ transform: "scale(1.15)", cursor: "pointer" }}
                />
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
                <Pill color={colorForBias(r.bias)}>{r.bias}</Pill>
                <span style={{ color: playColor(playLabel), fontWeight: 900 }}>{playLabel} · {playScore}</span>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, fontSize: 14 }}>
                <div><span style={{ color: "#A0A0A0" }}>Contract</span><br /><b>{contractLabel(r)}</b></div>
                <div><span style={{ color: "#A0A0A0" }}>Price</span><br /><b>${fmt(r.price, 2)}</b></div>
                <div><span style={{ color: "#A0A0A0" }}>MSI</span><br /><b>{Math.round(Number(r.msi_score || 0))}</b></div>
                <div><span style={{ color: "#A0A0A0" }}>Opp</span><br /><b style={{ color: opportunityColor(r) }}>{opportunityValue(r)}</b></div>
                <div style={{ gridColumn: "1 / -1" }}><span style={{ color: "#A0A0A0" }}>Target</span><br /><b>{fmtTarget(r.target)}</b></div>
                <div><span style={{ color: "#A0A0A0" }}>Structure</span><br /><b>{fmtStage(r.recovery_3d_stage)}</b></div>
                <div><span style={{ color: "#A0A0A0" }}>Candle</span><br /><b style={{ color: candleColor(candleDisplay(r)) }}>{candleDisplay(r)}</b></div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#0B0B0B", color: "white", padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16, alignItems: "center" }}>
        <div>
          <h1 style={{ fontSize: 40, marginBottom: 4 }}>MSI Universe</h1>
          <div style={{ color: "#A0A0A0" }}>
            Browse all market structure signals from your current universe.
          </div>
        </div>

        <a href="/msi" style={{ color: "#A0A0A0", textDecoration: "none" }}>
          ← Back to Dashboard
        </a>
      </div>

      <MSINav />

      <div style={{ ...panel, marginBottom: 20 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.15fr 0.7fr 1fr 1fr 0.85fr 0.7fr 1fr",
            gap: 12,
            marginBottom: 12,
          }}
        >
          <input
            style={inputStyle}
            placeholder="Search ticker, sector, setup, pattern..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />

          <select style={inputStyle} value={bias} onChange={(e) => setBias(e.target.value)}>
            {["ALL", "CALL", "PUT", "WAIT"].map((b) => (
              <option key={b}>{b}</option>
            ))}
          </select>

          <select style={inputStyle} value={pattern} onChange={(e) => setPattern(e.target.value)}>
            {patterns.map((p) => (
              <option key={p} value={p}>
                {patternLabel(p)}
              </option>
            ))}
          </select>

          <select style={inputStyle} value={patternTf} onChange={(e) => setPatternTf(e.target.value)}>
            <option value="ALL">Pattern TF: ALL</option>
            <option value="weekly">Weekly</option>
            <option value="tf3d">3D</option>
            <option value="daily">Daily</option>
            <option value="tf4h">4H</option>
          </select>

          <select
            style={inputStyle}
            value={chartBias}
            onChange={(e) => setChartBias(e.target.value)}
          >
            <option value="ALL">Chart: ALL</option>
            <option value="BULLISH">Bullish</option>
            <option value="BEARISH">Bearish</option>
            <option value="NEUTRAL">Neutral</option>
          </select>

          <select style={inputStyle} value={minMsi} onChange={(e) => setMinMsi(e.target.value)}>
            <option value="ALL">MSI: ALL</option>
            <option value="90">MSI 90+</option>
            <option value="80">MSI 80+</option>
            <option value="70">MSI 70+</option>
            <option value="60">MSI 60+</option>
          </select>

          <select style={inputStyle} value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="option_score">Sort: Option Score</option>
            <option value="msi_score">Sort: MSI Score</option>
            <option value="confidence">Sort: Confluence</option>
            <option value="candle_score">Sort: Candle Score</option>
            <option value="recovery_3d_score">Sort: 3D Recovery</option>
            <option value="recovery_3d_expansion_room_pct">Sort: Opportunity</option>
            <option value="confluence_score">Sort: Setup Confluence</option>
            <option value="price">Sort: Price</option>
          </select>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
          <select style={inputStyle} value={sector} onChange={(e) => setSector(e.target.value)}>
            {sectors.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: 12, color: "#A0A0A0" }}>
        Showing {filtered.length} of {rows.length} tickers
      </div>

      <div
        style={{
          ...panel,
          marginBottom: 14,
          display: "flex",
          gap: 12,
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <div style={{ color: "#A0A0A0", fontWeight: 800 }}>
          Selected Plays: <span style={{ color: "white" }}>{selectedCount}</span>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <button onClick={() => setViewMode("grid")} style={{
            background: viewMode === "grid" ? "#2563eb" : "#262626",
            color: "white",
            border: "1px solid #3A3A3A",
            borderRadius: 8,
            padding: "9px 12px",
            fontWeight: 800,
            cursor: "pointer",
          }}>
            Grid
          </button>

          <button onClick={() => setViewMode("cards")} style={{
            background: viewMode === "cards" ? "#2563eb" : "#262626",
            color: "white",
            border: "1px solid #3A3A3A",
            borderRadius: 8,
            padding: "9px 12px",
            fontWeight: 800,
            cursor: "pointer",
          }}>
            Cards
          </button>

          <button onClick={() => setShowColumns((v) => !v)} style={{
            background: showColumns ? "#2563eb" : "#262626",
            color: "white",
            border: "1px solid #3A3A3A",
            borderRadius: 8,
            padding: "9px 12px",
            fontWeight: 800,
            cursor: "pointer",
          }}>
            Columns
          </button>

          <button
            onClick={selectVisible}
            style={{
              background: "#262626",
              color: "white",
              border: "1px solid #3A3A3A",
              borderRadius: 8,
              padding: "9px 12px",
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            Select Visible
          </button>

          <button
            onClick={clearSelected}
            style={{
              background: "#262626",
              color: "white",
              border: "1px solid #3A3A3A",
              borderRadius: 8,
              padding: "9px 12px",
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            Clear
          </button>

          <button
            onClick={addSelectedToWatchlist}
            disabled={!selectedCount}
            style={{
              background: selectedCount ? "#f59e0b" : "#262626",
              color: "white",
              border: "none",
              borderRadius: 8,
              padding: "9px 14px",
              fontWeight: 900,
              cursor: selectedCount ? "pointer" : "not-allowed",
              opacity: selectedCount ? 1 : 0.55,
            }}
          >
            Add Watchlist
          </button>

          <button
            onClick={addSelectedPositions}
            disabled={!selectedCount}
            style={{
              background: selectedCount ? "#16a34a" : "#262626",
              color: "white",
              border: "none",
              borderRadius: 8,
              padding: "9px 14px",
              fontWeight: 900,
              cursor: selectedCount ? "pointer" : "not-allowed",
              opacity: selectedCount ? 1 : 0.55,
            }}
          >
            Add Position
          </button>
        </div>
      </div>

      {showColumns && (
        <div style={{ ...panel, marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
            <div style={{ fontWeight: 900 }}>Column Preferences</div>
            <button onClick={resetColumns} style={{
              background: "#262626",
              color: "white",
              border: "1px solid #3A3A3A",
              borderRadius: 8,
              padding: "6px 10px",
              fontWeight: 800,
              cursor: "pointer",
            }}>
              Reset
            </button>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {COLUMN_DEFS.filter((c) => !c.locked).map((c) => (
              <label key={c.key} style={{ color: "#D4D4D4", fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={!!visibleCols[c.key]}
                  onChange={() => toggleColumn(c.key)}
                  style={{ marginRight: 6 }}
                />
                {c.label}
              </label>
            ))}
          </div>
        </div>
      )}

      {viewMode === "cards" ? (
        <CardView />
      ) : (
        <div style={{ ...panel, overflowX: "auto" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: grid,
              minWidth,
              gap: 12,
              color: "#A0A0A0",
              fontWeight: 800,
              marginBottom: 8,
              borderBottom: "1px solid #2A2A2A",
              paddingBottom: 10,
              ...compactText,
            }}
          >
            {activeColumns.map((c) => (
              <div key={c.key}>{c.label}</div>
            ))}
          </div>

          {filtered.map((r) => (
            <div key={`${r.ticker}-${r.sector}-${r.setup_type}`}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: grid,
                  minWidth,
                  gap: 12,
                  alignItems: "center",
                  padding: "10px 0",
                  borderBottom: "1px solid #242424",
                  ...compactText,
                }}
              >
                {activeColumns.map((c) => (
                  <Cell key={c.key} col={c} row={r} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
